import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, KeyRound, Layers3, Pencil, Plus, Search, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { getErrorMessage } from '@/lib/api'
import { compareFeatureLabels, formatFeatureLabel } from '@/lib/feature-labels'
import { isAssignmentLockedFeature } from '@/lib/policy-feature-assignment'
import {
  getServiceInstanceIdFromSearch,
  getServiceInstanceIdFromToken,
  getServiceInstanceScopeLabel,
  getServiceInstanceScopeLabelFromInstance,
  withServiceInstanceId,
} from '@/lib/service-instance'
import { isOrganizationServiceVariant, isRootOrganizationServiceVariant } from '@/lib/service-variant-types'
import { getSidebarFeatureLabel } from '@/lib/sidebar-feature-map'
import { orgTreeApi } from '@/services/org-tree'
import { policyFeaturesApi, type PolicyFeature } from '@/services/policy-features'
import { useAuthStore } from '@/stores/auth-store'

type FeatureFormState = {
  id: number | null
  feature: string
  sidebarKey: string
  parentId: string
  sortOrder: string
  serviceVariants: string[]
}

type PendingFeatureDelete = {
  id: number
  feature: string
} | null

type FeatureTreeNode = {
  feature: PolicyFeature
  children: FeatureTreeNode[]
}

const initialFormState: FeatureFormState = {
  id: null,
  feature: '',
  sidebarKey: '',
  parentId: 'root',
  sortOrder: '0',
  serviceVariants: [],
}

const resolveCurrentRoleId = (currentPost: ReturnType<typeof useAuthStore.getState>['currentPost']) => {
  if (!currentPost) {
    return null
  }

  return currentPost.role_id || currentPost.roles_data?.id || null
}

const toWalletTerminology = (value: string | null | undefined) => {
  const text = String(value || '')
  if (!text) {
    return ''
  }

  return text
    .replace(/payment methods/gi, 'wallets')
    .replace(/payment method/gi, 'wallet')
    .replace(/payment_methods/gi, 'wallets')
    .replace(/payment_method/gi, 'wallet')
    .replace(/\bPayments\b/g, 'Wallets')
    .replace(/\bPayment\b/g, 'Wallet')
}

export default function FeaturesPage() {
  const location = useLocation()
  const queryClient = useQueryClient()
  const accessToken = useAuthStore((state) => state.accessToken)
  const storedServiceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const currentPost = useAuthStore((state) => state.currentPost)
  const currentPostServiceInstanceId = currentPost?.service_instance
    ? String(currentPost.service_instance)
    : currentPost?.service_instance_id
      ? String(currentPost.service_instance_id)
      : null
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
    || storedServiceInstanceId
    || getServiceInstanceIdFromToken(accessToken)
  const [search, setSearch] = useState('')
  const [expandedFeatureGroups, setExpandedFeatureGroups] = useState<Record<string, boolean>>({})
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [featureForm, setFeatureForm] = useState<FeatureFormState>(initialFormState)
  const [pendingDelete, setPendingDelete] = useState<PendingFeatureDelete>(null)
  const canManageRoles = Boolean(currentPost?.capabilities?.can_manage_roles)
  const currentRoleId = resolveCurrentRoleId(currentPost)
  const currentVariantUniqueId = String(currentPost?.service_variant_unique_id ?? '').trim().toLowerCase()
  const currentBusinessType = String(currentPost?.service_variant_name || currentPost?.service_variant_type || '').trim().toLowerCase()
  const isOrganizationRoleContext = isOrganizationServiceVariant(currentVariantUniqueId, currentBusinessType)
  const isRootOrgRoleContext = isRootOrganizationServiceVariant(currentVariantUniqueId, currentBusinessType)
  const [selectedServiceVariant, setSelectedServiceVariant] = useState('')

  const serviceVariantsQuery = useQuery({
    queryKey: ['feature-service-variants', serviceInstanceId],
    queryFn: () => orgTreeApi.listFeatureScopeVariants(serviceInstanceId),
    enabled: isOrganizationRoleContext && Boolean(serviceInstanceId),
    staleTime: 60_000,
  })

  const permissibleServiceVariants = useMemo(() => {
    if (!isOrganizationRoleContext) {
      return []
    }

    return serviceVariantsQuery.data ?? []
  }, [isOrganizationRoleContext, serviceVariantsQuery.data])

  useEffect(() => {
    if (!isOrganizationRoleContext) {
      if (selectedServiceVariant) {
        setSelectedServiceVariant('')
      }
      return
    }

    if (permissibleServiceVariants.length === 0) {
      return
    }

    setSelectedServiceVariant((current) => {
      if (!current) {
        const currentContextVariant = String(currentPost?.service_variant_unique_id ?? '').trim()
        if (currentContextVariant && permissibleServiceVariants.some((option) => String(option.id) === currentContextVariant)) {
          return currentContextVariant
        }
      }

      if (current && permissibleServiceVariants.some((option) => String(option.id) === current)) {
        return current
      }

      return String(permissibleServiceVariants[0].id)
    })
  }, [currentPost?.service_variant_unique_id, isOrganizationRoleContext, permissibleServiceVariants, selectedServiceVariant])

  const effectiveServiceVariant = isOrganizationRoleContext
    ? (selectedServiceVariant || null)
    : String(currentPost?.service_variant_unique_id || '') || null
  const effectiveServiceInstanceId = serviceInstanceId || null
  const selectedServiceVariantOption = permissibleServiceVariants.find((option) => String(option.id) === effectiveServiceVariant)
  const selectedServiceTypeLabel = selectedServiceVariantOption?.name || 'App Type'
  const canManageFeatureCatalog = canManageRoles && isRootOrgRoleContext
  const isManagingSpecificBranch = Boolean(serviceInstanceId) && Boolean(currentPostServiceInstanceId) && serviceInstanceId !== currentPostServiceInstanceId

  const featuresScopeInstanceQuery = useQuery({
    queryKey: ['features-scope-label-instance', effectiveServiceInstanceId],
    queryFn: () => orgTreeApi.getOptionalServiceInstance(effectiveServiceInstanceId!),
    enabled: Boolean(effectiveServiceInstanceId),
    staleTime: 60_000,
  })

  const featuresScopeLabel = useMemo(() => {
    if (featuresScopeInstanceQuery.data) {
      return getServiceInstanceScopeLabelFromInstance(featuresScopeInstanceQuery.data)
    }
    return getServiceInstanceScopeLabel(
      currentPost?.service_variant_unique_id,
      currentPost?.service_variant_name ?? currentPost?.service_variant_type,
      currentPost?.level_variant_name,
    )
  }, [
    featuresScopeInstanceQuery.data,
    currentPost?.level_variant_name,
    currentPost?.service_variant_name,
    currentPost?.service_variant_type,
    currentPost?.service_variant_unique_id,
  ])

  const featuresScopeLabelLower = featuresScopeLabel.toLowerCase()

  const featuresQuery = useQuery({
    queryKey: ['policy-features', currentRoleId, effectiveServiceVariant, effectiveServiceInstanceId],
    queryFn: () => policyFeaturesApi.listCurrentService({
      roleId: currentRoleId!,
      serviceVariant: effectiveServiceVariant,
      serviceInstanceId: effectiveServiceInstanceId,
    }),
    enabled: Boolean(currentRoleId) && (Boolean(effectiveServiceVariant) || Boolean(effectiveServiceInstanceId)),
  })

  const features = featuresQuery.data?.features ?? []
  const serviceId = featuresQuery.data?.serviceId ?? null
  const selectedServiceOwnerLabel = selectedServiceVariantOption?.serviceName || features[0]?.serviceName || null

  const filteredFeatures = useMemo(() => {
    const searchValue = search.trim().toLowerCase()
    return features
      .filter((feature) => {
        if (!searchValue) {
          return true
        }

        return (
          feature.feature.toLowerCase().includes(searchValue)
          || formatFeatureLabel(feature.feature).toLowerCase().includes(searchValue)
          || String(feature.featureUniqueId || '').toLowerCase().includes(searchValue)
          || String(feature.sidebarKey || '').toLowerCase().includes(searchValue)
        )
      })
      .slice()
      .sort((first, second) => {
        const orderDiff = (first.sortOrder ?? 0) - (second.sortOrder ?? 0)
        return orderDiff || compareFeatureLabels(first, second)
      })
  }, [features, search])

  const featureTree = useMemo(() => {
    const featuresById = new Map(features.map((feature) => [feature.id, feature]))
    const filteredIds = new Set(filteredFeatures.map((feature) => feature.id))
    const childrenByParentId = new Map<number, PolicyFeature[]>()
    const rootFeatures: PolicyFeature[] = []
    const searchValue = search.trim().toLowerCase()

    features.forEach((feature) => {
      if (feature.parentId && featuresById.has(feature.parentId)) {
        const children = childrenByParentId.get(feature.parentId) ?? []
        children.push(feature)
        childrenByParentId.set(feature.parentId, children)
        return
      }

      rootFeatures.push(feature)
    })

    const sortFeatures = (items: PolicyFeature[]) => items.slice().sort((first, second) => {
      const orderDiff = (first.sortOrder ?? 0) - (second.sortOrder ?? 0)
      return orderDiff || compareFeatureLabels(first, second)
    })

    const hasVisibleDescendant = (feature: PolicyFeature): boolean => {
      if (filteredIds.has(feature.id)) {
        return true
      }

      return (childrenByParentId.get(feature.id) ?? []).some(hasVisibleDescendant)
    }

    const buildNode = (feature: PolicyFeature): FeatureTreeNode => {
      const featureMatches = filteredIds.has(feature.id)
      const children = sortFeatures(childrenByParentId.get(feature.id) ?? [])
        .filter((child) => !searchValue || featureMatches || hasVisibleDescendant(child))
        .map(buildNode)

      return { feature, children }
    }

    return sortFeatures(rootFeatures)
      .filter((feature) => hasVisibleDescendant(feature))
      .map(buildNode)
  }, [features, filteredFeatures, search])

  const childrenByFeatureId = useMemo(() => {
    const next = new Map<number, PolicyFeature[]>()
    features.forEach((feature) => {
      if (!feature.parentId) {
        return
      }

      const children = next.get(feature.parentId) ?? []
      children.push(feature)
      next.set(feature.parentId, children)
    })
    return next
  }, [features])

  const parentFeatureOptions = useMemo(
    () => {
      const excludedIds = new Set<number>()
      if (featureForm.id) {
        const collectDescendants = (featureId: number) => {
          excludedIds.add(featureId)
          ;(childrenByFeatureId.get(featureId) ?? []).forEach((child) => collectDescendants(child.id))
        }
        collectDescendants(featureForm.id)
      }

      return features
        .filter((feature) => !excludedIds.has(feature.id))
        .slice()
        .sort((first, second) => {
        const orderDiff = (first.sortOrder ?? 0) - (second.sortOrder ?? 0)
        return orderDiff || compareFeatureLabels(first, second)
      })
    },
    [childrenByFeatureId, featureForm.id, features],
  )

  const closeDialog = () => {
    setIsDialogOpen(false)
    setFeatureForm(initialFormState)
  }

  const getFormParentId = () => {
    if (featureForm.parentId === 'root') {
      return null
    }

    const parentId = Number(featureForm.parentId)
    return Number.isFinite(parentId) ? parentId : null
  }

  const getFormSortOrder = () => {
    const sortOrder = Number(featureForm.sortOrder)
    return Number.isFinite(sortOrder) ? sortOrder : 0
  }

  const createFeatureMutation = useMutation({
    mutationFn: () => {
      if (!serviceId) {
        throw new Error('Missing service context.')
      }

      return policyFeaturesApi.create({
        serviceId,
        feature: featureForm.feature.trim(),
        sidebarKey: featureForm.sidebarKey.trim() || null,
        parentId: getFormParentId(),
        sortOrder: getFormSortOrder(),
        serviceVariantIds: featureForm.serviceVariants,
      })
    },
    onSuccess: () => {
      toast.success('Feature created successfully')
      closeDialog()
      queryClient.invalidateQueries({ queryKey: ['policy-features'] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const updateFeatureMutation = useMutation({
    mutationFn: () => {
      if (!featureForm.id) {
        throw new Error('Missing feature id.')
      }

      return policyFeaturesApi.update({
        id: featureForm.id,
        feature: featureForm.feature.trim(),
        sidebarKey: featureForm.sidebarKey.trim(),
        parentId: getFormParentId(),
        sortOrder: getFormSortOrder(),
        serviceVariantIds: featureForm.serviceVariants,
      })
    },
    onSuccess: () => {
      toast.success('Feature updated successfully')
      closeDialog()
      queryClient.invalidateQueries({ queryKey: ['policy-features'] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const deleteFeatureMutation = useMutation({
    mutationFn: (id: number) => policyFeaturesApi.remove(id),
    onSuccess: () => {
      toast.success('Feature deleted successfully')
      setPendingDelete(null)
      queryClient.invalidateQueries({ queryKey: ['policy-features'] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const updateBranchVisibilityMutation = useMutation({
    mutationFn: ({ id, branchHidden }: { id: number; branchHidden: boolean }) => {
      if (!serviceInstanceId) {
        throw new Error('Missing service branch context.')
      }

      return policyFeaturesApi.update({
        id,
        serviceInstanceId,
        branchHidden,
      })
    },
    onSuccess: () => {
      toast.success('Feature visibility updated for this Service Branch')
      queryClient.invalidateQueries({ queryKey: ['policy-features'] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const openCreateDialog = () => {
    setFeatureForm({
      ...initialFormState,
      parentId: 'root',
      sortOrder: '0',
      serviceVariants: effectiveServiceVariant ? [String(effectiveServiceVariant)] : [],
    })
    setIsDialogOpen(true)
  }

  const openEditDialog = (feature: PolicyFeature) => {
    setFeatureForm({
      id: feature.id,
      feature: feature.feature,
      sidebarKey: feature.sidebarKey || '',
      parentId: feature.parentId ? String(feature.parentId) : 'root',
      sortOrder: String(feature.sortOrder ?? 0),
      serviceVariants: (feature.serviceVariants ?? []).map((variant) => String(variant.variantUniqueId ?? variant.id)),
    })
    setIsDialogOpen(true)
  }

  const handleSave = () => {
    if (featureForm.id) {
      updateFeatureMutation.mutate()
      return
    }

    createFeatureMutation.mutate()
  }

  const toggleFeatureVariantSelection = (variantId: string, checked: boolean) => {
    setFeatureForm((current) => ({
      ...current,
      serviceVariants: checked
        ? Array.from(new Set([...current.serviceVariants, variantId]))
        : current.serviceVariants.filter((value) => value !== variantId),
    }))
  }

  const renderFeatureNode = (node: FeatureTreeNode, depth = 1) => {
    const { feature, children } = node
    const isChild = depth > 0
    const hasChildren = children.length > 0
    const groupKey = String(feature.id)
    const isExpanded = expandedFeatureGroups[groupKey] ?? true
    const nestedPadding = isChild ? `${1.75 + Math.max(depth - 1, 0) * 1.25}rem` : undefined
    const featureLabel = toWalletTerminology(formatFeatureLabel(feature.feature)) || formatFeatureLabel(feature.feature)
    const sidebarLabel = getSidebarFeatureLabel(feature.sidebarKey)

    return (
      <div key={feature.id}>
        <div
          className={`border-t border-border/60 p-4 ${isChild ? 'bg-muted/20' : 'bg-background/70'}`}
          style={{ paddingLeft: nestedPadding }}
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                {hasChildren ? (
                  <button
                    type="button"
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border/70 bg-background/80 text-muted-foreground"
                    onClick={() => setExpandedFeatureGroups((current) => ({ ...current, [groupKey]: !isExpanded }))}
                    aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${featureLabel}`}
                  >
                    {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  </button>
                ) : null}
                <span className="font-medium text-foreground">{featureLabel}</span>
                {isChild ? (
                  <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">Child</Badge>
                ) : (
                  <Badge variant="outline" className="border-violet-200 bg-violet-50 text-violet-700">Parent</Badge>
                )}
                {hasChildren ? (
                  <Badge variant="outline" className="border-border/70 bg-muted/40 text-muted-foreground">
                    {children.length} child{children.length === 1 ? '' : 'ren'}
                  </Badge>
                ) : null}
                {feature.disabledByParent ? (
                  <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">Disabled by parent</Badge>
                ) : null}
                {feature.branchHidden ? (
                  <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-700">Hidden for this Service Branch</Badge>
                ) : null}
              </div>
              <div className="text-sm text-muted-foreground">
                {isChild && feature.parentFeature
                  ? `Under ${toWalletTerminology(formatFeatureLabel(feature.parentFeature)) || formatFeatureLabel(feature.parentFeature)}`
                  : feature.serviceName
                    ? `Owned by ${feature.serviceName}`
                    : 'Owned by current service'}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {isManagingSpecificBranch ? (
                <label className="flex items-center gap-2 rounded-lg border border-border/60 bg-background px-3 py-2 text-sm text-muted-foreground">
                  <Checkbox
                    checked={isAssignmentLockedFeature(feature) || !feature.branchHidden}
                    disabled={
                      isAssignmentLockedFeature(feature)
                      || !canManageFeatureCatalog
                      || updateBranchVisibilityMutation.isPending
                    }
                    onCheckedChange={(checked) => {
                      if (isAssignmentLockedFeature(feature)) {
                        return
                      }

                      updateBranchVisibilityMutation.mutate({
                        id: feature.id,
                        branchHidden: checked !== true,
                      })
                    }}
                  />
                  {isAssignmentLockedFeature(feature)
                    ? 'Always enabled'
                    : feature.branchHidden
                      ? 'Hidden'
                      : 'Visible'}
                </label>
              ) : null}
              {canManageFeatureCatalog ? (
                <>
                  <Button variant="outline" size="sm" className="rounded-lg" onClick={() => openEditDialog(feature)}>
                    <Pencil className="mr-2 h-3.5 w-3.5" />
                    Edit
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-lg text-destructive" onClick={() => setPendingDelete({ id: feature.id, feature: featureLabel })}>
                    <Trash2 className="mr-2 h-3.5 w-3.5" />
                    Delete
                  </Button>
                </>
              ) : null}
            </div>
          </div>

          <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2 xl:grid-cols-4">
            <div>
              <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Unique id</p>
              <p className="mt-1 text-foreground">{feature.featureUniqueId || 'Not assigned'}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Variant scope</p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {(feature.serviceVariants ?? []).length > 0 ? (
                  (feature.serviceVariants ?? []).map((variant) => (
                    <Badge key={`${feature.id}-${variant.id}`} variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">
                      {variant.name || variant.variantUniqueId || 'Variant'}
                    </Badge>
                  ))
                ) : (
                  <span className="text-muted-foreground">All variants</span>
                )}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Sidebar mapping</p>
              <p className="mt-1 text-foreground">{sidebarLabel ? `Controls sidebar: ${toWalletTerminology(sidebarLabel)}` : 'No sidebar mapping'}</p>
              {feature.sidebarKey ? <p className="text-xs text-muted-foreground">{feature.sidebarKey}</p> : null}
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Sort order</p>
              <p className="mt-1 text-foreground">{feature.sortOrder ?? 0}</p>
            </div>
          </div>
        </div>
        {hasChildren && isExpanded ? <div>{children.map((child) => renderFeatureNode(child, depth + 1))}</div> : null}
      </div>
    )
  }

  if (!serviceInstanceId) {
    return (
      <div className="flex h-64 items-center justify-center rounded-[28px] border border-border/70 bg-card/80 text-center text-muted-foreground">
        Missing organization context. Open this page with a service_instance_id query parameter.
      </div>
    )
  }

  if (!currentRoleId) {
    return (
      <div className="flex h-64 items-center justify-center rounded-[28px] border border-border/70 bg-card/80 text-center text-muted-foreground">
        Select a role first before opening the feature catalog.
      </div>
    )
  }

  return (
    <div className="space-y-6 pb-2">
      <section className="grid gap-4 xl:grid-cols-[minmax(0,1.6fr)_minmax(320px,0.8fr)]">
        <Card className="overflow-hidden rounded-[30px] border-white/70 bg-[linear-gradient(135deg,rgba(255,255,255,0.98),rgba(239,246,255,0.92))] shadow-[0_24px_60px_-36px_rgba(15,23,42,0.45)]">
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-2xl space-y-4">
                <Badge className="rounded-full border-0 bg-sky-500/10 px-3 py-1 text-sky-700 hover:bg-sky-500/10">
                  <KeyRound className="mr-2 h-3.5 w-3.5" />
                  Feature catalog
                </Badge>
                <div className="space-y-2">
                  <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">Features</h1>
                  <p className="max-w-xl text-sm leading-6 text-muted-foreground sm:text-base">
                    Root ORG controls which features belong to each app variant and which sidebar capability key, if any, each feature controls.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <Button variant="outline" className="rounded-lg" asChild>
                  <Link to={withServiceInstanceId('/org/roles', serviceInstanceId)}>Back to Roles</Link>
                </Button>
                {canManageFeatureCatalog ? (
                  <Button className="rounded-lg" onClick={openCreateDialog}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Feature
                  </Button>
                ) : null}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4 sm:grid-cols-3 xl:grid-cols-1">
          <Card className="rounded-[26px] border-white/70 bg-card/85 shadow-sm">
            <CardContent className="flex items-center gap-4 p-5">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Layers3 className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Visible features</p>
                <p className="text-2xl font-semibold text-foreground">{filteredFeatures.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-[26px] border-white/70 bg-card/85 shadow-sm">
            <CardContent className="flex items-center gap-4 p-5">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                <KeyRound className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Sidebar-linked</p>
                <p className="text-2xl font-semibold text-foreground">{filteredFeatures.filter((feature) => Boolean(feature.sidebarKey)).length}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-[26px] border-white/70 bg-card/85 shadow-sm">
            <CardContent className="flex items-center gap-4 p-5">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
                <Pencil className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Access</p>
                <p className="text-lg font-semibold text-foreground">{canManageFeatureCatalog ? 'Editable' : 'Read only'}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Card className="rounded-[30px] border-white/70 bg-card/88 shadow-[0_20px_50px_-40px_rgba(15,23,42,0.5)] backdrop-blur">
        <CardHeader className="gap-4 border-b border-border/60 pb-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
                      <CardTitle className="text-xl font-semibold">{selectedServiceTypeLabel} Features</CardTitle>
              <CardDescription>
                Sidebar visibility is now driven from the feature rows in the database. Leave sidebar key empty for non-navigation features.
                {selectedServiceOwnerLabel && selectedServiceTypeLabel !== selectedServiceOwnerLabel ? ` ${selectedServiceTypeLabel} belongs to ${selectedServiceOwnerLabel}, so mapped features can still be owned by that parent service.` : ''}
              </CardDescription>
            </div>

            <div className="flex w-full max-w-2xl flex-col gap-3 sm:flex-row sm:items-end sm:justify-end">
              {isOrganizationRoleContext ? (
                <div className="w-full min-w-[220px] space-y-1 sm:max-w-xs">
                      <Label htmlFor="features-service-filter">App type</Label>
                      <Select value={selectedServiceVariant} onValueChange={setSelectedServiceVariant}>
                    <SelectTrigger id="features-service-filter" aria-label="App type" className="h-11 rounded-xl border-border/70 bg-muted/40">
                      <SelectValue placeholder={serviceVariantsQuery.isLoading ? 'Loading app types...' : 'Select an app type'} />
                    </SelectTrigger>
                    <SelectContent>
                      {permissibleServiceVariants.map((option) => (
                        <SelectItem key={String(option.id)} value={String(option.id)}>{option.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}

              <div className="relative w-full max-w-sm">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search feature, unique id, or sidebar key"
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-4 sm:p-6">
          {isOrganizationRoleContext && serviceVariantsQuery.isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="flex items-center gap-4 rounded-2xl border border-border/60 bg-muted/30 p-4">
                  <Skeleton className="h-10 w-full rounded-xl" />
                </div>
              ))}
            </div>
          ) : isOrganizationRoleContext && permissibleServiceVariants.length === 0 ? (
            <div className="flex h-40 items-center justify-center rounded-[24px] border border-border/70 bg-background/75 px-6 text-center text-muted-foreground">
              No service types are available for this {featuresScopeLabelLower} yet.
            </div>
          ) : isOrganizationRoleContext && !effectiveServiceVariant ? (
            <div className="flex h-40 items-center justify-center rounded-[24px] border border-border/70 bg-background/75 px-6 text-center text-muted-foreground">
              Select a service type to view features.
            </div>
          ) : featuresQuery.error ? (
            <div className="flex h-40 items-center justify-center rounded-[24px] border border-destructive/20 bg-background/75 px-6 text-center text-muted-foreground">
              Failed to load features: {getErrorMessage(featuresQuery.error)}
            </div>
          ) : featuresQuery.isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="flex items-center gap-4 rounded-2xl border border-border/60 bg-muted/30 p-4">
                  <Skeleton className="h-10 w-10 rounded-xl" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-44" />
                    <Skeleton className="h-3 w-60" />
                  </div>
                  <Skeleton className="h-8 w-24 rounded-full" />
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {featureTree.length > 0 ? (
                  featureTree.map((node) => {
                    const { feature: parent, children } = node
                    const groupKey = String(parent.id)
                    const isExpanded = expandedFeatureGroups[groupKey] ?? true
                    const parentLabel = toWalletTerminology(formatFeatureLabel(parent.feature)) || formatFeatureLabel(parent.feature)
                    const sidebarLabel = getSidebarFeatureLabel(parent.sidebarKey)

                    return (
                      <div key={parent.id} className="overflow-hidden rounded-[24px] border border-border/70 bg-background/75">
                        <div className="p-4 sm:p-5">
                          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                            <button
                              type="button"
                              className="flex min-w-0 flex-1 items-start gap-3 text-left"
                              onClick={() => setExpandedFeatureGroups((current) => ({ ...current, [groupKey]: !isExpanded }))}
                            >
                              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border/70 bg-muted/35 text-muted-foreground">
                                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              </span>
                              <span className="min-w-0 space-y-2">
                                <span className="flex flex-wrap items-center gap-2">
                                  <span className="font-semibold text-foreground">{parentLabel}</span>
                                  <Badge variant="outline" className="border-violet-200 bg-violet-50 text-violet-700">Parent</Badge>
                                  {children.length > 0 ? (
                                    <Badge variant="outline" className="border-border/70 bg-muted/40 text-muted-foreground">
                                      {children.length} child{children.length === 1 ? '' : 'ren'}
                                    </Badge>
                                  ) : null}
                                  {parent.branchHidden ? (
                                    <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-700">Hidden for this Service Branch</Badge>
                                  ) : null}
                                </span>
                                <span className="block text-sm text-muted-foreground">
                                  {sidebarLabel ? `Controls sidebar: ${toWalletTerminology(sidebarLabel)}` : 'No sidebar mapping'}
                                  {parent.sidebarKey ? ` (${parent.sidebarKey})` : ''}
                                </span>
                              </span>
                            </button>

                            <div className="flex flex-wrap items-center gap-2">
                              {isManagingSpecificBranch ? (
                                <label className="flex items-center gap-2 rounded-lg border border-border/60 bg-background px-3 py-2 text-sm text-muted-foreground">
                                  <Checkbox
                                    checked={!parent.branchHidden}
                                    disabled={!canManageFeatureCatalog || updateBranchVisibilityMutation.isPending}
                                    onCheckedChange={(checked) => {
                                      updateBranchVisibilityMutation.mutate({
                                        id: parent.id,
                                        branchHidden: checked !== true,
                                      })
                                    }}
                                  />
                                  {parent.branchHidden ? 'Hidden' : 'Visible'}
                                </label>
                              ) : null}
                              {canManageFeatureCatalog ? (
                                <>
                                  <Button variant="outline" size="sm" className="rounded-lg" onClick={() => openEditDialog(parent)}>
                                    <Pencil className="mr-2 h-3.5 w-3.5" />
                                    Edit
                                  </Button>
                                  <Button variant="outline" size="sm" className="rounded-lg text-destructive" onClick={() => setPendingDelete({ id: parent.id, feature: parentLabel })}>
                                    <Trash2 className="mr-2 h-3.5 w-3.5" />
                                    Delete
                                  </Button>
                                </>
                              ) : null}
                            </div>
                          </div>

                          <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2 xl:grid-cols-4">
                            <div>
                              <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Unique id</p>
                              <p className="mt-1 text-foreground">{parent.featureUniqueId || 'Not assigned'}</p>
                            </div>
                            <div>
                              <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Variant scope</p>
                              <div className="mt-1 flex flex-wrap gap-1.5">
                                {(parent.serviceVariants ?? []).length > 0 ? (
                                  (parent.serviceVariants ?? []).map((variant) => (
                                    <Badge key={`${parent.id}-${variant.id}`} variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">
                                      {variant.name || variant.variantUniqueId || 'Variant'}
                                    </Badge>
                                  ))
                                ) : (
                                  <span className="text-muted-foreground">All variants</span>
                                )}
                              </div>
                            </div>
                            <div>
                              <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Children</p>
                              <p className="mt-1 text-foreground">{parent.childrenCount ?? children.length}</p>
                            </div>
                            <div>
                              <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Sort order</p>
                              <p className="mt-1 text-foreground">{parent.sortOrder ?? 0}</p>
                            </div>
                          </div>
                        </div>

                        {isExpanded ? (
                          children.length > 0 ? (
                            <div>{children.map((child) => renderFeatureNode(child, 1))}</div>
                          ) : (
                            <div className="border-t border-border/60 bg-muted/20 p-4 text-sm text-muted-foreground">
                              No child features under this parent.
                            </div>
                          )
                        ) : null}
                      </div>
                    )
                  })
                ) : (
                  <div className="flex h-24 items-center justify-center rounded-[24px] border border-border/70 bg-background/75 text-center text-muted-foreground">
                    No features matched the current filter.
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={isDialogOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            closeDialog()
            return
          }

          setIsDialogOpen(nextOpen)
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{featureForm.id ? 'Edit Feature' : 'Add Feature'}</DialogTitle>
            <DialogDescription>
              Update the feature label, the service variants that expose it, and the optional sidebar capability key stored on this feature row.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="feature-name">Feature name</Label>
              <Input
                id="feature-name"
                value={featureForm.feature}
                onChange={(event) => setFeatureForm((current) => ({ ...current, feature: event.target.value }))}
                placeholder="Ex: Tenant"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="feature-sidebar-key">Sidebar key</Label>
              <Input
                id="feature-sidebar-key"
                value={featureForm.sidebarKey}
                onChange={(event) => setFeatureForm((current) => ({ ...current, sidebarKey: event.target.value }))}
                placeholder="Ex: can_view_tenant_directory"
              />
              <p className="text-xs text-muted-foreground">
                Leave empty if this feature should stay in the catalog but not drive a sidebar item.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_140px]">
              <div className="space-y-2">
                <Label htmlFor="feature-parent">Parent feature</Label>
                <Select
                  value={featureForm.parentId}
                  onValueChange={(value) => setFeatureForm((current) => ({ ...current, parentId: value }))}
                >
                  <SelectTrigger id="feature-parent" className="h-11 rounded-xl border-border/70 bg-muted/40">
                    <SelectValue placeholder="Select parent" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="root">No parent</SelectItem>
                    {parentFeatureOptions.map((feature) => (
                      <SelectItem key={feature.id} value={String(feature.id)}>
                        {toWalletTerminology(formatFeatureLabel(feature.feature)) || formatFeatureLabel(feature.feature)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Children are disabled when their parent feature is unavailable or not granted.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="feature-sort-order">Sort order</Label>
                <Input
                  id="feature-sort-order"
                  type="number"
                  value={featureForm.sortOrder}
                  onChange={(event) => setFeatureForm((current) => ({ ...current, sortOrder: event.target.value }))}
                  placeholder="0"
                />
              </div>
            </div>

            <div className="space-y-3 rounded-xl border border-border/70 bg-muted/20 p-4">
              <div>
                <Label>Service variants</Label>
                <p className="mt-1 text-xs text-muted-foreground">
                  Choose which service variants should expose this feature.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {permissibleServiceVariants.length > 0 ? (
                  permissibleServiceVariants.map((variant) => {
                    const variantId = String(variant.id)
                    const checked = featureForm.serviceVariants.includes(variantId)

                    return (
                      <label key={variantId} className="flex items-start gap-3 rounded-lg border border-border/60 bg-background/70 p-3 text-sm">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(nextChecked) => toggleFeatureVariantSelection(variantId, nextChecked === true)}
                        />
                        <span className="font-medium text-foreground">{variant.name}</span>
                      </label>
                    )
                  })
                ) : (
                  <p className="text-sm text-muted-foreground">No service type options are available for this {featuresScopeLabelLower} yet.</p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={createFeatureMutation.isPending || updateFeatureMutation.isPending || !featureForm.feature.trim() || featureForm.serviceVariants.length === 0}
            >
              {featureForm.id ? 'Save changes' : 'Create feature'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={Boolean(pendingDelete)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setPendingDelete(null)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete feature</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete
                ? `This will remove ${toWalletTerminology(pendingDelete.feature) || pendingDelete.feature} if it is not already used by policy actions.`
                : 'Delete this feature.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (pendingDelete) {
                  deleteFeatureMutation.mutate(pendingDelete.id)
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}